import type { ReactNode } from 'react'
import { fbt } from 'fbtee'
import invariant from 'tiny-invariant'
import { ExternalLink, MapPin, Martini, Pencil } from 'lucide-react'
import { formatClockTime, parseDateOnly, toBcp47 } from '@shared/dates'
import { Button } from './ui/button'
import { Skeleton } from './ui/skeleton'
import { cn } from '../lib/utils'
import { friendFallbackName } from '../lib/commonStrings'
import type { Pregame, UserProfile } from '../types'

const CONTAINER = 'rounded-lg border border-border bg-card p-3 text-sm'

/** Someone's pre-game for an event: host, when, where (as a Maps link) and notes. */
export function PregameCard({ loading, pregame, host, hostLoading, isMine, locale, onEdit, footer }: {
  loading?: boolean
  pregame?: Pregame
  host?: UserProfile
  /** The host's profile is still loading — their name shows as a skeleton. */
  hostLoading?: boolean
  isMine?: boolean
  locale?: string
  /** The host's own card only. */
  onEdit?: () => void
  /** Under the details, e.g. who's going. */
  footer?: ReactNode
}) {
  if (loading) {
    return (
      <div className={cn(CONTAINER, 'flex items-center gap-3')}>
        <Skeleton className="h-14 w-16 rounded-md" />
        <div className="flex-1 space-y-2">
          <Skeleton className="h-4 w-36" />
          <Skeleton className="h-3.5 w-44" />
        </div>
      </div>
    )
  }

  invariant(pregame, 'PregameCard: pregame is required when not loading')
  invariant(locale, 'PregameCard: locale is required when not loading')
  const weekday = pregame.day
    ? parseDateOnly(pregame.day).toLocaleDateString(toBcp47(locale), { weekday: 'short' })
    : null
  const time = pregame.time ? formatClockTime(pregame.time, locale) : null
  const mapsUrl = pregame.address ? `https://maps.google.com/?q=${encodeURIComponent(pregame.address)}` : null

  const pin = <MapPin className="size-3.5 shrink-0 text-muted-foreground" />
  const mapsIcon = <ExternalLink className="size-3 shrink-0 text-muted-foreground" />
  const hostedBy = isMine ? null : hostLoading && !host ? (
    <Skeleton className="h-3 w-16 shrink-0" />
  ) : (
    <span className="shrink-0 text-xs font-normal text-muted-foreground">
      · {fbt('by ' + fbt.param('name', host?.displayName ?? friendFallbackName()), 'Who hosts a pre-game, after its place on the pre-game card')}
    </span>
  )
  const place = (
    <>
      <div className="flex min-w-0 items-center gap-1.5 text-base font-semibold leading-tight">
        {!pregame.placeName && pregame.address && pin}
        <span className="truncate">
          {pregame.placeName || pregame.address ||
            fbt('Place to be announced', 'Pre-game card title when the host gave no place yet')}
        </span>
        {!pregame.placeName && pregame.address && mapsIcon}
        {hostedBy}
      </div>
      {pregame.placeName && pregame.address && (
        <p className="mt-1 flex items-center gap-1 text-muted-foreground">
          {pin}
          <span className="truncate">{pregame.address}</span>
          {mapsIcon}
        </p>
      )}
    </>
  )

  return (
    <div className={CONTAINER} data-testid="pregame-card">
      <div className="flex items-center gap-3">
        <div className="flex h-14 w-16 shrink-0 flex-col items-center justify-center rounded-md bg-primary/10 text-primary">
          {weekday && <span className="text-[10px] font-semibold uppercase tracking-wide">{weekday}</span>}
          {time ? (
            <span className="text-sm font-bold leading-tight">{time}</span>
          ) : (
            !weekday && <Martini className="size-5" />
          )}
        </div>
        {mapsUrl ? (
          <a href={mapsUrl} target="_blank" rel="noopener noreferrer" className="min-w-0 flex-1">
            {place}
          </a>
        ) : (
          <div className="min-w-0 flex-1">{place}</div>
        )}
        {onEdit && (
          <Button
            variant="ghost"
            size="icon-sm"
            className="-mr-1 self-start"
            onClick={onEdit}
            aria-label={fbt('Edit pre-game', "Aria label on the button that edits the viewer's pre-game")}
          >
            <Pencil />
          </Button>
        )}
      </div>

      {pregame.notes && (
        <p className="mt-3 whitespace-pre-line break-words border-l-2 border-border pl-2.5 text-muted-foreground">
          {pregame.notes}
        </p>
      )}
      {footer}
    </div>
  )
}
